import React, { useContext, useEffect, useState } from "react"
import { useHistory } from "react-router-dom"
import { useAlert } from "react-alert"
import { confirmAlert } from "react-confirm-alert"

// @material-ui/core components
import { makeStyles } from "@material-ui/core/styles"
import Paper from "@material-ui/core/Paper"
import Tooltip from "@material-ui/core/Tooltip"
import IconButton from "@material-ui/core/IconButton"
import Chip from "@material-ui/core/Chip"

// @material-ui/icons
import {
  Assignment,
  Dvr,
  DeleteForever,
  Event,
  Add,
  Edit,
  CheckCircle,
  Warning,
  Info,
  Visibility,
  DateRange,
} from "@material-ui/icons"

// Private core components
import GridContainer from "components/Grid/GridContainer.js"
import GridItem from "components/Grid/GridItem.js"
import Button from "components/CustomButtons/Button.js"
import ReactTable from "components/ReactTable/ReactTable.js"

// Context & API
import authContext from "../../../context/AuthContext"
import { eventList, removeEvents } from "../../../api/eventAction"
import { removeAllMediaRef } from "../../../api/ImageActions"
import config from "../../../config"
import { removeHTMLTags } from "../../../utils/functions"

// Styling
import "react-confirm-alert/src/react-confirm-alert.css"

const localStyles = {
  mainContainer: {
    background: "linear-gradient(135deg, #667eea 0%, #0d142b 100%)",
    minHeight: "100vh",
    padding: "40px 20px",
    borderRadius: "12px",
  },
  contentWrapper: {
    maxWidth: "1400px",
    margin: "0 auto",
  },
  headerCard: {
    background: "white",
    borderRadius: "16px",
    padding: "32px",
    marginBottom: "24px",
    boxShadow: "0 10px 40px rgba(0,0,0,0.1)",
  },
  pageTitle: {
    fontSize: "32px",
    fontWeight: "700",
    color: "#2d3748",
    marginBottom: "8px",
    display: "flex",
    alignItems: "center",
    gap: "12px",
  },
  pageSubtitle: {
    fontSize: "16px",
    color: "#718096",
    fontWeight: "400",
    marginBottom: "24px",
  },
  statsRow: {
    display: "flex",
    gap: "16px",
    marginTop: "24px",
    flexWrap: "wrap",
  },
  statCard: {
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    color: "white",
    padding: "20px",
    borderRadius: "12px",
    flex: "1",
    minWidth: "200px",
    boxShadow: "0 4px 15px rgba(102, 126, 234, 0.3)",
  },
  statNumber: {
    fontSize: "32px",
    fontWeight: "700",
    marginBottom: "4px",
  },
  statLabel: {
    fontSize: "14px",
    opacity: "0.9",
  },
  tableCard: {
    background: "white",
    borderRadius: "16px",
    padding: "32px",
    boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
  },
  sectionHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: "24px",
    paddingBottom: "16px",
    borderBottom: "2px solid #e2e8f0",
  },
  sectionTitle: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
  },
  sectionIcon: {
    width: "40px",
    height: "40px",
    borderRadius: "10px",
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "white",
  },
  titleText: {
    fontSize: "24px",
    fontWeight: "600",
    color: "#2d3748",
    margin: 0,
  },
  addButton: {
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    color: "white",
    padding: "12px 24px",
    borderRadius: "10px",
    border: "none",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "600",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    boxShadow: "0 4px 15px rgba(102, 126, 234, 0.4)",
    transition: "transform 0.2s, box-shadow 0.2s",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 6px 20px rgba(102, 126, 234, 0.5)",
    },
  },
  actionButtons: {
    display: "flex",
    gap: "8px",
    alignItems: "center",
  },
  actionButton: {
    width: "36px",
    height: "36px",
    borderRadius: "8px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    transition: "all 0.2s",
    border: "none",
    "&:hover": {
      transform: "scale(1.1)",
    },
  },
  editButton: {
    background: "#3b82f6",
    color: "white",
    "&:hover": {
      background: "#2563eb",
    },
  },
  deleteButton: {
    background: "#ef4444",
    color: "white",
    "&:hover": {
      background: "#dc2626",
    },
  },
  statusBadge: {
    padding: "4px 12px",
    borderRadius: "12px",
    fontSize: "12px",
    fontWeight: "600",
    display: "flex",
    alignItems: "center",
    gap: "4px",
  },
  statusActive: {
    background: "#d1fae5",
    color: "#065f46",
  },
  statusSuspended: {
    background: "#fee2e2",
    color: "#991b1b",
  },
  statusDraft: {
    background: "#fef3c7",
    color: "#92400e",
  },
  eventImage: {
    width: "60px",
    height: "60px",
    borderRadius: "8px",
    objectFit: "cover",
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
  },
  infoBox: {
    background: "#eff6ff",
    border: "2px solid #bfdbfe",
    borderRadius: "12px",
    padding: "16px",
    marginBottom: "24px",
    display: "flex",
    alignItems: "flex-start",
    gap: "12px",
  },
  infoIcon: {
    color: "#3b82f6",
    marginTop: "2px",
  },
  infoText: {
    fontSize: "14px",
    color: "#1e40af",
    lineHeight: "1.6",
    margin: 0,
  },
  docLink: {
    color: "#667eea",
    textDecoration: "none",
    fontSize: "14px",
    fontWeight: "600",
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    "&:hover": {
      textDecoration: "underline",
    },
  },
  visitsChip: {
    background: "#e0e7ff",
    color: "#3730a3",
    fontWeight: "600",
    fontSize: "12px",
    padding: "4px 8px",
    borderRadius: "8px",
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
  },
}

const useStyles = makeStyles(localStyles)

const label = {
  pageTitle: "Gestion des Événements",
  pageSubtitle:
    "Gérez tous vos événements, modifiez leurs informations et surveillez leur statut",
  totalEvents: "Total événements",
  activeEvents: "Actifs",
  draftEvents: "Brouillons",
  tableTitle: "Liste des événements",
  addEvent: "Nouvel événement",
  messageOK: "Bravo, modification effectuée !",
  messageKO: "Aïe, veuillez réessayer ou contacter l'administrateur.",
  confirmDelete: "Êtes-vous sûr de vouloir supprimer",
  confirmDeleteQuestion: "?",
  yes: "Oui",
  no: "Non",
  edit: "Modifier",
  delete: "Supprimer",
  active: "Actif",
  suspended: "Suspendu",
  draft: "Brouillon",
  visits: "Visites",
  infoMessage:
    "Créez et gérez vos événements. Les événements en brouillon ne sont pas visibles par les utilisateurs.",
  documentation: "Voir la documentation",
}

const BASE_URL = config.BASE_URL

export function EventList() {
  const { mpId } = useContext(authContext)
  const [event, setEvent] = useState(undefined)
  const [filter, setFilter] = useState({ draft: 0, running: 2, last7: 0, group: 0 })
  const [stats, setStats] = useState({ total: 0, active: 0, draft: 0 })
  const history = useHistory()
  const alert = useAlert()
  const classes = useStyles()

  useEffect(() => {
    if (mpId) loadEvents(0, 2, 0, 0)
  }, [mpId])

  const loadEvents = (_draft = 0, _running = 2, _last7 = 0, _group = 0) => {
    eventList(mpId, _running, _group, _draft, "az", _last7)
      .then((response) => {
        if (response.status === 200 && response.data.status === 1) {
          let st = response.data.data
          if (st.length > 0) {
            console.log("Events loaded :", st)

            // Calculer les statistiques
            const totalEvents = st.length
            const activeEvents = st.filter((e) => !e.suspended && !e.is_draft).length
            const draftEvents = st.filter((e) => e.is_draft).length

            setStats({
              total: totalEvents,
              active: activeEvents,
              draft: draftEvents,
            })
            console.log("load data", st)
            setEvent(
              st.map((v) => {
                return {
                  cover: v.cover,
                  ev_id: v.ev_id,
                  cid: v.cid,
                  c_name: v.c_name,
                  ev_name: v.ev_name,
                  details: v.details ? removeHTMLTags(v.details) : "",
                  dt_from: v.dt_from,
                  dt_to: v.dt_to,
                  city: v.city,
                  is_free: v.is_free,
                  tarif: v.tarif + "\\" + v.tarif_promo,
                  suspended: v.suspended,
                  visits: v.visits,
                  draft: v.is_draft,
                  actions: actionButton(st, v.ev_id, v.suspended, v.is_draft),
                  isFirebase: v.is_firebase ?? 0,
                  coverUrl: v.url ?? null,
                }
              })
            )
          } else {
            setEvent([])
            setStats({ total: 0, active: 0, draft: 0 })
          }
        }
      })
      .catch((e) => console.log(e))
  }

  const changeFilter = (_filterName, _filterValue) => {
    let st = filter

    if (_filterName)
      switch (_filterName) {
        case "draft":
          st.draft = _filterValue ? 2 : 0
          break
        case "last7":
          st.last7 = _filterValue ? 1 : 0
          break
        case "running":
          st.running = _filterValue ? 1 : 2
          break
      }
    setFilter(st)
    loadEvents(st.draft, st.running, st.last7, null)
  }

  const removeAllMediasRefOnly = async (eventId) => {
    try {
      console.log(
        "Suppression des références aux médias pour l'événement ID :",
        eventId
      )
      await removeAllMediaRef("event", eventId, true)
    } catch (error) {
      console.error("Erreur suppression ancienne cover:", error)
    }
  }

  const handleDelete = (_newData, _id, _eventName) => {
    confirmAlert({
      title: label.confirmDelete,
      message: `${label.confirmDelete} ${_eventName} ${label.confirmDeleteQuestion}`,
      buttons: [
        {
          label: label.yes,
          onClick: () => {
            removeEvents(_id)
              .then(async (response) => {
                if (response === "ok") {
                  await removeAllMediasRefOnly(_id)
                  loadEvents(
                    filter.draft,
                    filter.running,
                    filter.last7,
                    filter.group
                  )
                  alert.success(label.messageOK)

                  // Mettre à jour les stats
                  setStats((prev) => ({
                    ...prev,
                    total: prev.total - 1,
                  }))
                }
              })
              .catch((error) => {
                console.log(error)
                alert.error(label.messageKO)
              })
          },
        },
        {
          label: label.no,
          onClick: () => {},
        },
      ],
    })
  }

  const actionButton = (_event, _id, _suspended = false, _isDraft = false) => {
    return (
      <div className={classes.actionButtons}>
        <Tooltip title={label.edit}>
          <button
            className={`${classes.actionButton} ${classes.editButton}`}
            onClick={() => {
              let obj = _event.find((o) => o.ev_id === _id)
              if (obj) {
                const path = "/admin/event-manager"
                history.push({
                  pathname: path,
                  state: { evId: obj.ev_id, cid: obj.cid },
                })
              }
            }}
          >
            <Edit style={{ fontSize: "18px" }} />
          </button>
        </Tooltip>

        <Tooltip title={label.delete}>
          <button
            className={`${classes.actionButton} ${classes.deleteButton}`}
            onClick={() => {
              let newData = _event
              let eventName = ""
              newData.find((o, i) => {
                if (o.ev_id === _id) {
                  newData.splice(i, 1)
                  eventName = o.ev_name
                  return true
                }
                return false
              })

              newData.map((obj) => {
                obj.actions = actionButton(
                  newData,
                  obj.ev_id,
                  obj.suspended,
                  obj.draft
                )
              })
              handleDelete(newData, _id, eventName)
            }}
          >
            <DeleteForever style={{ fontSize: "18px" }} />
          </button>
        </Tooltip>

        <Tooltip
          title={
            _isDraft ? label.draft : _suspended ? label.suspended : label.active
          }
        >
          <div
            className={`${classes.statusBadge} ${
              _isDraft
                ? classes.statusDraft
                : _suspended
                ? classes.statusSuspended
                : classes.statusActive
            }`}
          >
            {_isDraft ? (
              <Warning style={{ fontSize: "16px" }} />
            ) : _suspended ? (
              <Warning style={{ fontSize: "16px" }} />
            ) : (
              <CheckCircle style={{ fontSize: "16px" }} />
            )}
          </div>
        </Tooltip>
      </div>
    )
  }

  const handleAddEvent = () => {
    const path = "/admin/event-manager"
    history.push({ pathname: path })
  }

  return (
    <div className={classes.mainContainer}>
      <div className={classes.contentWrapper}>
        {/* Header */}
        <div className={classes.headerCard}>
          <div className={classes.pageTitle}>
            <Event style={{ fontSize: "40px", color: "#667eea" }} />
            {label.pageTitle}
          </div>
          <p className={classes.pageSubtitle}>{label.pageSubtitle}</p>

          {/* Statistiques */}
          <div className={classes.statsRow}>
            <div className={classes.statCard}>
              <div className={classes.statNumber}>{stats.total}</div>
              <div className={classes.statLabel}>{label.totalEvents}</div>
            </div>
            <div className={classes.statCard}>
              <div className={classes.statNumber}>{stats.active}</div>
              <div className={classes.statLabel}>{label.activeEvents}</div>
            </div>
            <div className={classes.statCard}>
              <div className={classes.statNumber}>{stats.draft}</div>
              <div className={classes.statLabel}>{label.draftEvents}</div>
            </div>
          </div>
        </div>

        {/* Info box */}
        <div className={classes.infoBox}>
          <Info className={classes.infoIcon} />
          <div>
            <p className={classes.infoText}>{label.infoMessage}</p>
            <a
              href="/faq#menu-7"
              target="_blank"
              rel="noopener noreferrer"
              className={classes.docLink}
            >
              {label.documentation} →
            </a>
          </div>
        </div>

        {/* Table */}
        <Paper className={classes.tableCard}>
          <div className={classes.sectionHeader}>
            <div className={classes.sectionTitle}>
              <div className={classes.sectionIcon}>
                <Assignment />
              </div>
              <h3 className={classes.titleText}>{label.tableTitle}</h3>
            </div>
            <button className={classes.addButton} onClick={handleAddEvent}>
              <Add style={{ fontSize: "20px" }} />
              {label.addEvent}
            </button>
          </div>

          {event ? (
            <ReactTable
              filter={(x, y) => {
                changeFilter(x, y)
              }}
              name="event"
              columns={[
                {
                  Header: "Visuel",
                  Cell: (tableProps) => (
                    <div>
                      <img
                        alt="Événement"
                        className={classes.eventImage}
                        src={
                          tableProps.row.original.isFirebase === 1 &&
                          tableProps.row.original.coverUrl
                            ? tableProps.row.original.coverUrl
                            : BASE_URL +
                              "img/events/" +
                              tableProps.row.original.cover
                        }
                      />
                    </div>
                  ),
                },
                { Header: "Nom", accessor: "ev_name" },
                { Header: "Établissement", accessor: "c_name" },
                {
                  Header: "Description",
                  accessor: (d) => {
                    return d.details ? d.details.substring(0, 60) + "..." : null
                  },
                },
                { Header: "Début", accessor: "dt_from" },
                { Header: "Fin", accessor: "dt_to" },
                { Header: "Tarif\\promo", accessor: "tarif" },
                {
                  Header: "Visites",
                  Cell: (tableProps) => (
                    <span className={classes.visitsChip}>
                      <Visibility style={{ fontSize: "14px" }} />
                      {tableProps.row.original.visits}
                    </span>
                  ),
                },
                { Header: "Actions", accessor: "actions" },
              ]}
              data={event}
            />
          ) : null}
        </Paper>
      </div>
    </div>
  )
}

// import React, { useContext, useEffect, useState } from "react"
// import { useHistory } from "react-router-dom"
// import { useAlert } from "react-alert"
//
// // @material-ui/core components
// import { makeStyles } from "@material-ui/core/styles"
// // @material-ui/icons
// import Assignment from "@material-ui/icons/Assignment"
// import Dvr from "@material-ui/icons/Dvr"
// import Close from "@material-ui/icons/Close"
//
// // core components
// import GridContainer from "components/Grid/GridContainer.js"
// import GridItem from "components/Grid/GridItem.js"
// import Button from "components/CustomButtons/Button.js"
// import Card from "components/Card/Card.js"
// import CardBody from "components/Card/CardBody.js"
// import CardIcon from "components/Card/CardIcon.js"
// import CardHeader from "components/Card/CardHeader.js"
// import ReactTable from "components/ReactTable/ReactTable.js"
//
// // Private Component
// import { cardTitle } from "assets/jss/material-dashboard-pro-react.js"
// import { eventList, removeEvents } from "../../../api/eventAction"
// import authContext from "../../../context/AuthContext"
// import config from "../../../config"
// import { removeHTMLTags } from "../../../utils/functions"
// import { DeleteForever } from "@material-ui/icons"
// import { confirmAlert } from "react-confirm-alert"
// import { removeCompanyAndUser } from "../../../api/companyAction"
// import Favorite from "@material-ui/icons/Favorite"
// import { setFilterMemo } from "../../../services/LocaleStorage"
// import { deleteImage, removeAllMediaRef } from "../../../api/ImageActions"
//
// const styles = {
//   cardIconTitle: {
//     ...cardTitle,
//     marginTop: "15px",
//     marginBottom: "0px",
//   },
// }
//
// const label = {
//   messageOK: "Bravo, modification effectuée !",
//   messageKO: "Aie, veuillez réessayer ou contacter l'administrateur.",
// }
//
// const BASE_URL = config.BASE_URL
// const useStyles = makeStyles(styles)
//
// export function EventList() {
//   const { mpId } = useContext(authContext)
//   const [event, setEvent] = useState(undefined)
//   const [filter, setFilter] = useState({ draft: 0, running: 2, last7: 0, group: 0 })
//   const history = useHistory()
//   const alert = useAlert()
//
//   useEffect(() => {
//     if (mpId) loadEvents(0, 2, 0, 0)
//   }, [mpId])
//
//   /**
//    * Note :
//    * @param _draft
//    * @param _running
//    * @param _last7
//    * @param _group
//    */
//   const loadEvents = (_draft = 0, _running = 2, _last7 = 0, _group = 0) => {
//     eventList(mpId, _running, _group, _draft, "az", _last7)
//       .then((response) => {
//         if (response.status === 200 && response.data.status === 1) {
//           let st = response.data.data
//           if (st.length > 0) {
//             console.log("Events loaded :", st)
//             setEvent(
//               st.map((v) => {
//                 // prepare data for actions buttons (!)
//                 // todo : algo for delete option
//                 return {
//                   cover: v.cover,
//                   ev_id: v.ev_id,
//                   cid: v.cid,
//                   c_name: v.c_name,
//                   ev_name: v.ev_name,
//                   details: v.details ? removeHTMLTags(v.details) : "",
//                   dt_from: v.dt_from,
//                   dt_to: v.dt_to,
//                   city: v.city,
//                   is_free: v.is_free,
//                   tarif: v.tarif + "\\" + v.tarif_promo,
//                   suspended: v.suspended,
//                   visits: v.visits,
//                   draft: v.is_draft,
//                   actions: actionButton(st, v.ev_id),
//                   isFirebase: v.is_firebase ?? 0,
//                   coverUrl: v.url ?? null,
//                 }
//               })
//             )
//           } else {
//             setEvent([])
//           }
//         }
//       })
//       .catch((e) => console.log(e))
//   }
//
//   /**
//    * Note : Manage filter and reload data
//    * @param _filter
//    */
//   const changeFilter = (_filterName, _filterValue) => {
//     let st = filter
//
//     if (_filterName)
//       switch (_filterName) {
//         case "draft":
//           st.draft = _filterValue ? 2 : 0
//           break
//         case "last7":
//           st.last7 = _filterValue ? 1 : 0
//           break
//         case "running":
//           st.running = _filterValue ? 1 : 2
//           break
//       }
//     // Update state value for next use memory
//     setFilter(st)
//     loadEvents(st.draft, st.running, st.last7, null)
//   }
//
//   /**
//    * Supprimer Les references aux medias dans la DB uniquement
//    */
//   const removeAllMediasRefOnly = async (eventId) => {
//     try {
//       console.log(
//         "Suppression des références aux médias pour l'événement ID :",
//         eventId
//       )
//       await removeAllMediaRef("event", eventId, true)
//     } catch (error) {
//       console.error("Erreur suppression ancienne cover:", error)
//     }
//   }
//
//   /**
//    *
//    * @param _newData
//    * @param _id
//    * @param _eventName
//    */
//   const handleDelete = (_newData, _id, _eventName) => {
//     console.log("handleDelete", _id, _eventName, _newData)
//     confirmAlert({
//       title: "Confirmer",
//       message: `êtes vous sûr de vouloir supprimer ${_eventName} ?`,
//       buttons: [
//         {
//           label: "Oui",
//           onClick: () => {
//             removeEvents(_id)
//               .then(async (response) => {
//                 if (response === "ok") {
//                   await removeAllMediasRefOnly(_id)
//                   console.log(
//                     "Références aux médias supprimées pour l'événement ID :",
//                     _id
//                   )
//                   console.log("nouveau data :", _newData)
//                   // setEvent([_newData])
//                   loadEvents(
//                     filter.draft,
//                     filter.running,
//                     filter.last7,
//                     filter.group
//                   )
//                   alert.success(label.messageOK)
//                 }
//               })
//               .catch((error) => {
//                 console.log(error)
//                 alert.error(label.messageKO)
//               })
//           },
//         },
//         {
//           label: "Non",
//           onClick: () => {},
//         },
//       ],
//     })
//   }
//
//   /**
//    *
//    * @param _event
//    * @param _id
//    * @returns {JSX.Element}
//    */
//   const actionButton = (_event, _id) => {
//     return (
//       <div className="actions-right">
//         <Button
//           justIcon
//           round
//           simple
//           onClick={() => {
//             let obj = _event.find((o) => o.ev_id === _id)
//             if (obj) {
//               const path = "/admin/event-manager"
//               history.push({
//                 pathname: path,
//                 state: { evId: obj.ev_id, cid: obj.cid },
//               })
//             } else alert("nothing found : " + obj)
//           }}
//           color="info"
//           className="edit"
//         >
//           <Dvr />
//         </Button>{" "}
//         {/* use this button to remove the data row */}
//         <Button
//           justIcon
//           round
//           simple
//           onClick={() => {
//             let newData = _event
//             let eventName = ""
//             newData.find((o, i) => {
//               if (o.ev_id === _id) {
//                 newData.splice(i, 1)
//                 eventName = o.ev_name
//                 return true
//               }
//               return false
//             })
//             newData.map((obj) => {
//               obj.actions = actionButton(newData, obj.ev_id)
//             })
//
//             // Removing event  from API _id === _cid
//             handleDelete(newData, _id, eventName)
//             // removeEvents(_id)
//             //   .then((response) => {
//             //     if (response === "ok") {
//             //       setEvent([...newData])
//             //       alert.success(label.messageOK)
//             //     }
//             //   })
//             //   .catch((error) => {
//             //     console.log(error)
//             //     alert.error(label.messageKO)
//             //   })
//           }}
//           color="danger"
//           className="remove"
//         >
//           <DeleteForever />
//         </Button>{" "}
//       </div>
//     )
//   }
//
//   const classes = useStyles()
//   return (
//     <GridContainer>
//       <GridItem xs={12}>
//         <Card>
//           <CardHeader color="primary" icon>
//             <CardIcon color="primary">
//               <Assignment />
//             </CardIcon>
//             <h4 className={classes.cardIconTitle}>Événéments</h4>
//             <Button
//               simple
//               href="/faq#menu-7"
//               target="_blank"
//               rel="noopener, noreferrer"
//               style={{
//                 right: 30,
//                 top: 10,
//                 position: "absolute",
//                 zIndex: 999,
//                 height: 25,
//                 backgroundColor: "transparent",
//                 color: "#494949",
//               }}
//             >
//               <h6 style={{ fontSize: "1em" }}>Documentation</h6>
//             </Button>
//           </CardHeader>
//           <CardBody>
//             {event ? (
//               <ReactTable
//                 filter={(x, y) => {
//                   changeFilter(x, y)
//                 }}
//                 name="event"
//                 columns={[
//                   {
//                     Header: "Visuel",
//                     Cell: (tableProps) => (
//                       <div>
//                         <img
//                           alt=".."
//                           style={{ height: 60 }}
//                           src={
//                             tableProps.row.original.isFirebase === 1 &&
//                             tableProps.row.original.coverUrl
//                               ? tableProps.row.original.coverUrl
//                               : BASE_URL +
//                                 "img/events/" +
//                                 tableProps.row.original.cover
//                           }
//                         />
//                       </div>
//                     ),
//                   },
//                   { Header: "Nom", accessor: "ev_name" },
//                   { Header: "établissment", accessor: "c_name" },
//                   {
//                     Header: "Description",
//                     accessor: (d) => {
//                       return d.details ? d.details.substring(0, 60) + "..." : null
//                     },
//                   },
//                   { Header: "Début", accessor: "dt_from" },
//                   { Header: "Fin", accessor: "dt_to" },
//                   { Header: "Tarif\\promo", accessor: "tarif" },
//                   {
//                     id: "suspended",
//                     Header: "Suspendu",
//                     accessor: (d) => (d.suspended ? "oui" : "non"),
//                   },
//                   { Header: "Visite", accessor: "visits" },
//                   { Header: "Draft", accessor: "draft" },
//                   { Header: "Actions", accessor: "actions" },
//                 ]}
//                 SubComponent={(row) => {
//                   return <div>PICTURES IN ROW</div>
//                 }}
//                 data={event}
//               />
//             ) : null}
//           </CardBody>
//         </Card>
//       </GridItem>
//     </GridContainer>
//   )
// }
